import {
  createSignedGameSnapshot,
  verifySignedGameSnapshot
} from './protocol.js';

export const MATCH_EXPORT_TYPE = 'dechess-match';
export const MATCH_EXPORT_SCHEMA_VERSION = 1;
export const MAX_MATCH_EXPORT_BYTES = 2 * 1024 * 1024;

const CONTINUABLE_STATUSES = new Set(['waiting', 'starting', 'playing']);

/**
 * Short stable id fragment for lists / export filenames.
 */
export function matchShortHash(game) {
  const id = String(game?.id || '').replace(/[^a-fA-F0-9]/g, '');
  if (id.length >= 8) {
    return id.slice(0, 8).toLowerCase();
  }
  return String(game?.id || 'match').slice(0, 8);
}

/**
 * Prefer clock start, then creation time (open invites that never began).
 */
export function matchIdentifiedAt(game) {
  if (Number.isFinite(game?.startedAt)) {
    return game.startedAt;
  }
  if (Number.isFinite(game?.createdAt)) {
    return game.createdAt;
  }
  if (Number.isFinite(game?.updatedAt)) {
    return game.updatedAt;
  }
  return null;
}

export function formatMatchIdentifiedAt(game, locale) {
  const at = matchIdentifiedAt(game);
  if (!Number.isFinite(at)) {
    return '';
  }
  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeStyle: 'short'
  }).format(at);
}

/**
 * Casual adjournments only. Rated games stay live (or reconnect via dual-signed
 * resume) so clocks and ladder integrity are not paused offline.
 */
export function allowsMatchExport(game) {
  return Boolean(game?.id && !game.rated && CONTINUABLE_STATUSES.has(game.status));
}

export function isContinuableMatch(game) {
  return allowsMatchExport(game);
}

export function buildMatchExport(game, identity = null) {
  if (!game?.id) {
    throw new Error('A game is required to export a match');
  }
  if (game.rated) {
    throw new Error('Rated games cannot be exported — finish or resign on the ladder');
  }
  if (!CONTINUABLE_STATUSES.has(game.status)) {
    throw new Error('Finished games cannot be exported for continue-later');
  }
  const envelope = {
    schemaVersion: MATCH_EXPORT_SCHEMA_VERSION,
    type: MATCH_EXPORT_TYPE,
    exportedAt: Date.now(),
    game
  };
  if (identity?.playerId && identity?.keyPair?.signing?.secretKey) {
    envelope.snapshot = createSignedGameSnapshot(game, identity);
  }
  return envelope;
}

export function matchExportFilename(game) {
  const shortId = matchShortHash(game);
  const revision = Number.isFinite(game?.revision) ? game.revision : 0;
  const at = matchIdentifiedAt(game);
  if (Number.isFinite(at)) {
    const stamp = new Date(at);
    const pad = (part) => String(part).padStart(2, '0');
    const day = `${stamp.getFullYear()}${pad(stamp.getMonth() + 1)}${pad(stamp.getDate())}-${pad(stamp.getHours())}${pad(stamp.getMinutes())}`;
    return `dechess-${shortId}-${day}-r${revision}.dechess.json`;
  }
  return `dechess-${shortId}-r${revision}.dechess.json`;
}

export function formatSavedMatchLabel(game, {
  opponentLabel,
  openSeatLabel,
  locale
} = {}) {
  const opponent = opponentLabel
    || openSeatLabel
    || 'open seat';
  const hash = matchShortHash(game);
  const when = formatMatchIdentifiedAt(game, locale);
  const revision = Number.isFinite(game?.revision) ? game.revision : 0;
  const parts = [
    game?.timeControl || '?',
    opponent,
    `#${hash}`
  ];
  if (when) {
    parts.push(when);
  }
  parts.push(`rev ${revision}`);
  return parts.join(' · ');
}

export function serializeMatchExport(envelope) {
  return `${JSON.stringify(envelope, null, 2)}\n`;
}

export function downloadMatchExport(envelope, filename = matchExportFilename(envelope.game)) {
  if (typeof document === 'undefined') {
    throw new Error('Match download requires a browser document');
  }
  const blob = new Blob([serializeMatchExport(envelope)], {
    type: 'application/json'
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
  return filename;
}

function assertGameShape(game) {
  if (!game || typeof game !== 'object') {
    throw new Error('Match export is missing a game');
  }
  if (!game.id || typeof game.id !== 'string') {
    throw new Error('Match export game id is invalid');
  }
  if (!game.players?.white?.playerId) {
    throw new Error('Match export is missing the white player');
  }
  if (!Array.isArray(game.moves)) {
    throw new Error('Match export moves are invalid');
  }
  if (!game.timeControl || typeof game.timeControl !== 'string') {
    throw new Error('Match export time control is invalid');
  }
  if (!game.fen || typeof game.fen !== 'string') {
    throw new Error('Match export fen is invalid');
  }
  return game;
}

export function parseMatchExport(raw) {
  if (typeof raw === 'string' && raw.length > MAX_MATCH_EXPORT_BYTES) {
    throw new Error('Match export file is too large');
  }
  let data = raw;
  if (typeof raw === 'string') {
    try {
      data = JSON.parse(raw);
    } catch {
      throw new Error('Match export file is not valid JSON');
    }
  }
  if (!data || typeof data !== 'object') {
    throw new Error('Match export file is invalid');
  }
  if (data.type !== MATCH_EXPORT_TYPE) {
    throw new Error('Not a deChess match export');
  }
  if (data.schemaVersion !== MATCH_EXPORT_SCHEMA_VERSION) {
    throw new Error('Unsupported match export version');
  }
  const game = assertGameShape(data.game);
  if (game.rated) {
    throw new Error('Rated match exports cannot be imported — only casual games pause');
  }
  if (!CONTINUABLE_STATUSES.has(game.status)) {
    throw new Error('Only in-progress casual matches can be imported');
  }
  if (data.snapshot) {
    if (!verifySignedGameSnapshot(data.snapshot)) {
      throw new Error('Match export snapshot signature is invalid');
    }
    if (data.snapshot.game?.id !== game.id) {
      throw new Error('Match export snapshot does not match the game');
    }
  }
  return {
    game,
    snapshot: data.snapshot || null,
    exportedAt: data.exportedAt || null
  };
}

export function roleForContinuingMatch(game, playerId) {
  if (game?.players?.white?.playerId === playerId) {
    return 'host';
  }
  if (game?.players?.black?.playerId === playerId) {
    return 'join';
  }
  return 'spectator';
}

export function continueMatchUrl(game, playerId, publicUrlFn) {
  const base = typeof publicUrlFn === 'function' ? publicUrlFn() : '/';
  const role = roleForContinuingMatch(game, playerId);
  const url = new URL(base, 'https://dechess.local');
  url.searchParams.set('game', game.id);
  url.searchParams.set('role', role);
  return `${base}?${url.searchParams.toString()}`;
}
